import { sendEventRegist } from 'helpers/analytics';
import {
  getChannelContextAdditionalInfo,
  getChannelContextCode
} from 'helpers/Utility';
import { setError, setIsLogin, setOauthToken } from 'store/actions/auth';

/**
 * Maps channel-context codes to handler functions for registerOTP error handling.
 * Each key returns a handler object with a handler function and whether to stop fallthrough.
 */
const ERROR_HANDLERS = {
  '105411': (_err, { callback }) => {
    callback?.('success');
    return true;
  },
  '211091': (_err, { callback }) => {
    callback?.('failed');
    return true;
  },
  '100474': (_err, { callback }) => {
    callback?.('limit_receive_otp');
    return true;
  },
  '5000101': (_err, { dispatch }) => {
    dispatch(setError('contact-cs'));
    sendEventRegist({
      action: 'Error while delivering the OTP',
      label: 'OTP'
    });
    dispatch(setOauthToken('', ''));
    dispatch(setIsLogin(false));
    return true;
  },
  '40000001': () => true,
};

/**
 * Handles special error cases that depend on both status code and channel-context.
 */
const handleSpecialCases = (err, status, context) => {
  // 401 status + code 105411
  if (status === 401 && context.code === '105411') {
    return 'success';
  }

  // 429 status + code 1008 → OTP freeze
  if (status === 429 && context.code === '1008') {
    const addInfo = getChannelContextAdditionalInfo(err, 'channel-context');
    const freezeTime = addInfo?.freezeEnd;
    localStorage.setItem('otpFreeze', JSON.stringify({ freezeTime }));
    return 'otp_temporary_locked';
  }

  return null;
};

/**
 * Handles registerOTP error responses.
 */
export const handleRegisterOtpError = (err, dispatch, callback) => {
  if (!err.response) {
    dispatch(setError('connection'));
    dispatch(setOauthToken('', ''));
    return;
  }

  const context = {
    code: getChannelContextCode(err, 'channel-context'),
    status: err.response.status,
  };

  // Check special combined cases first
  const specialResult = handleSpecialCases(err, context.status, context);
  if (specialResult === 'success') {
    callback?.('success');
    return;
  }
  if (specialResult === 'otp_temporary_locked') {
    dispatch(setError('otp_temporary_locked'));
    callback?.('otp_temporary_locked');
    return;
  }

  // Check mapped handlers
  const handlerFn = ERROR_HANDLERS[context.code];
  if (handlerFn) {
    handlerFn(err, { dispatch, callback });
    return;
  }

  // null context code → system error, reset auth
  if (context.code === null) {
    dispatch(setError('system'));
    dispatch(setOauthToken('', ''));
    return;
  }

  // Fallback: unknown error
  dispatch(setOauthToken('', ''));
  dispatch(setError('system'));
};
